import { firebaseAction } from '../utils'
import { accessListSelector } from '../selectors/accessSelectors'
import i18n from 'i18n'
import { userSelector } from '../selectors/baseSelectors'
import { getVal } from 'react-redux-firebase'
import { defaultBlockLimit } from 'variables'
import { showAlert, confirm } from './alerts'

export const moveItem = dragging =>
  firebaseAction(async (update, { getState, getAuth, getOnce, dispatch }) => {
    const auth = getAuth()
    const state = getState()
    const accessList = accessListSelector(state)

    // if you're trying to put a folder inside itself, stop right here
    let target = dragging.folder && accessList[dragging.folder]
    while (target) {
      if (target.id === dragging.id) return
      target = accessList[target.parent]
    }

    const dragItem = accessList[dragging.id]

    async function checkLimits() {
      if (!dragging.folder) return true

      const path = `/${dragItem.type}s/${dragItem.id}/blockCount`
      // try to get from state first (if this board/folder has been loaded)
      let blockCount = getVal(state.firebase, '/data/' + path)
      // if not in state, get from DB
      if (blockCount === undefined) blockCount = await getOnce(path)

      // if item has no blocks, move is allowed
      if (blockCount === 0) return true

      const ownsFolder =
        accessList[dragging.folder] && accessList[dragging.folder].isOwner

      // if moving within my items, move is allowed
      if (ownsFolder && dragItem.isOwner) return true

      const dragItemOwner = dragItem.isOwner
        ? auth.uid
        : await getOnce(`/folders/${dragItem.id}/owner/id`)

      let folderOwner
      // if I'm the owner, get block count from my profile
      if (ownsFolder) {
        folderOwner = userSelector(state)
      } else {
        const ownerId = await getOnce(`/folders/${dragging.folder}/owner/id`)

        // if moving within same owner, move is allowed
        if (dragItemOwner === ownerId) return true

        folderOwner = {
          blockCount: await getOnce(`/users/${ownerId}/blockCount`),
          blockLimit: await getOnce(`/users/${ownerId}/blockLimit`)
        }
      }

      // check block limit
      if (
        (folderOwner.blockCount || 0) + blockCount >=
        (folderOwner.blockLimit || defaultBlockLimit)
      ) {
        const owner = ownsFolder ? 'owner' : 'other'

        dispatch(showAlert(i18n.t(`alert.folderBlockLimitFull.${owner}`)))
        return false
      }

      return true
    }

    const canMove = await checkLimits()
    if (!canMove) return

    if (dragging.folder) {
      const confirmed = await new Promise(resolve => {
        dispatch(
          confirm('globalNav.confirmMove', resolve, {
            item: dragItem,
            folder: accessList[dragging.folder]
          })
        )
      })

      if (!confirmed) return
    }
    const toFolderId =
      dragging.folder === undefined ? dragItem.parent : dragging.folder

    let forward = {}

    const updateItemsPath = (item, folders) => {
      forward[`/${item.type}s/${item.id}/folders`] = folders
      Object.values(accessList).forEach(child => {
        if (child.parent === item.id) {
          updateItemsPath(child, [
            { id: item.id, name: item.name, color: item.color },
            ...folders
          ])
        }
      })
    }

    if (toFolderId !== dragItem.parent) {
      // if currently in a folder, remove. The next step will add it to new folder (if any)
      if (dragItem.parent) {
        // update parent in this user's access list for fast UI update
        forward[`/access/${auth.uid}/${dragging.id}/parent`] = null
        // remove parent
        updateItemsPath(dragItem, [])
        // remove from old folder
        forward[`/folders/${dragItem.parent}/children/${dragItem.id}`] = null
      }
      if (toFolderId) {
        // update parent in this user's access list for fast UI update
        forward[`/access/${auth.uid}/${dragging.id}/parent`] = toFolderId

        // add parent
        const folders = []
        let folder = { parent: toFolderId }
        while (folder.parent) {
          folder = accessList[folder.parent]
          folders.push({
            id: folder.id,
            name: folder.name,
            color: folder.color
          })
        }
        updateItemsPath(dragItem, folders)

        // add to new folder
        forward[`/folders/${toFolderId}/children/${dragItem.id}`] = {
          id: dragItem.id,
          name: dragItem.name,
          type: dragItem.type
        }
      }
    }

    if (toFolderId)
      forward = updateOrder(
        dragging.order,
        toFolderId,
        accessList,
        dragItem,
        forward,
        auth.uid
      )

    update({ forward })
  })

export const updateOrder = (
  newIndex = 0,
  folderId,
  accessList,
  dragItem,
  forward,
  userId
) => {
  folderId = folderId || dragItem.parent

  const sortOrder = Object.values(accessList)
    .filter(({ parent }) => parent === folderId)
    .sort((a, b) => b.sortOrder - a.sortOrder)

  const oldIndex = sortOrder.findIndex(i => i.id === dragItem.id)
  // if found, remove from current position
  if (oldIndex !== -1) {
    sortOrder.splice(oldIndex, 1) // remove from old index
    if (oldIndex <= newIndex) newIndex -= 1 // account for missing item
  }

  // insert at new index
  sortOrder.splice(newIndex, 0, dragItem)

  const length = sortOrder.length
  sortOrder.forEach((item, i) => {
    const order = length - i
    if (item.parent) {
      const path = `/folders/${folderId}/children/${item.id}`
      if (path in forward) forward[path].sortOrder = order
      else forward[path + '/sortOrder'] = order
    }
    forward[`/access/${userId}/${item.id}/sortOrder`] = order
  })

  return forward
}
